"use client";

import { useId, useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import type { StorefrontVideoDto } from "@tripcart/shared";
import {
  videoEmbedUrl,
  videoThumbnail,
} from "@tripcart/shared/video/video-source";

import styles from "./_reviews-media.module.css";

// ─── Reviews / Videos tabs — REMOVED 2026-09-15 ───────────────────────────────
//
// `ReviewsMediaTabs` used to split this section into "Reviews (110)" and
// "Videos (4)". The guide has no such control: the section reads top to bottom
// as score → video reviews → written reviews. A tab meant half the evidence
// was behind a click, and the count on it advertised exactly what it was
// hiding.

/**
 * Guide `.rvv` — video reviews, as a plain four-up grid between the rating
 * summary and the written reviews.
 *
 * NOT a tab, and not a carousel. Both were inventions: the guide gives the
 * reviews section one continuous reading order — score, then the videos, then
 * the written ones — and a tab hid half the evidence behind a click while a
 * carousel hid it behind a scroll. Someone deciding whether to book should see
 * everything other travellers said without operating a control first.
 */
export function VideoReviews({
  videos,
  heading,
  playLabel,
}: {
  videos: StorefrontVideoDto[];
  /** Rendered, but visually hidden — it names the region for assistive tech
   *  and is what `aria-labelledby` points at. Deleting it as "unused" would
   *  take the region's name with it. */
  heading: string;
  playLabel: string;
}) {
  const headingId = useId();
  if (videos.length === 0) return null;
  return (
    // `aria-labelledby`, NOT the heading alone. A `section` does not take its
    // name from a heading it merely contains — without this it stays an
    // unnamed generic and is not exposed as a region at all, which is the
    // thing the hidden heading was added to prevent.
    <section className={styles.section} aria-labelledby={headingId}>
      {/* VISUALLY HIDDEN, not deleted. SC #118 asked for the heading to go,
          and on screen it has. But an unnamed region left a screen reader
          arriving at a row of "Play Video" buttons straight after the ratings
          block with nothing saying what they are — they could as easily be the
          tour gallery. This keeps the name for the people who need it and
          takes it off the page for the people who asked. */}
      <h3 id={headingId} className="tui-sr-only">
        {heading}
      </h3>
      {/* Guide `.rvv-row` — THREE across at 20px, stepping to two then one.
          It was four at 16px, which is the FIRST `.rvv-row` in the guide's
          stylesheet; a later block ("Video reviews: 16:9 frame, centred
          title") redefines the whole component and wins. Four posters in the
          same rail made each one narrow enough that a 16:9 frame would have
          been under 140px wide. */}
      {/* On a phone the same three become the mobile guide's `.rvv-row`: a
          142px rail that runs off the right edge. Stacked one per row a
          16:9 poster is 200px tall, so four videos were 900px of scrolling
          before the first written review — the section's own evidence buried
          under its trailer reel. A rail shows all of them at a glance and says
          "there are more" by running off the edge. */}
      <div
        // NO scroll-snap. A snap point inside a rail that carries its own side
        // padding scrolls that padding out of view the moment it settles —
        // measured `scrollLeft: 16` on load, which clipped the first poster's
        // title to "nrise at Annapurna". The guide does not snap this rail,
        // and a peeking next card is the affordance anyway.
        className={styles.rail}
      >
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} playLabel={playLabel} />
        ))}
      </div>
    </section>
  );
}

function VideoCard({
  video,
  playLabel,
}: {
  video: StorefrontVideoDto;
  playLabel: string;
}) {
  const [playing, setPlaying] = useState(false);
  const embed = videoEmbedUrl(video.url, video.type);

  return (
    <figure className={styles.card}>
      {/* Guide `.rvv-f` — a 16:9 frame on a dark ground, 12px corners. It was
          a fixed 150px, justified as keeping every poster the same height; the
          ratio does that too, because the columns are equal width, and 16:9 is
          what the guide's own block is titled after. */}
      <div className={styles.frame}>
        {playing && embed ? (
          <iframe
            src={embed}
            title={video.title ?? playLabel}
            allow="autoplay; fullscreen; picture-in-picture"
            // Operator-supplied URLs ('other' type embeds the raw URL) — sandbox
            // blocks top-navigation/popups so a framed page can't phish from the
            // storefront chrome; allow-same-origin+scripts is what YT/Vimeo need.
            sandbox="allow-scripts allow-same-origin allow-presentation"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
            className={styles.player}
          />
        ) : (
          <VideoPoster
            video={video}
            playLabel={playLabel}
            onPlay={() => setPlaying(true)}
          />
        )}
      </div>
      {video.title && (
        // Guide `.rvv-title` — 15px, CENTRED, 11px below the frame, with two
        // lines reserved so the row's captions stay on one baseline. The
        // 13.5px left-aligned version was the guide's dead first definition.
        <figcaption className={styles.caption}>{video.title}</figcaption>
      )}
    </figure>
  );
}

function VideoPoster({
  video,
  playLabel,
  onPlay,
}: {
  video: StorefrontVideoDto;
  playLabel: string;
  onPlay: () => void;
}) {
  const thumb = videoThumbnail(video.url, video.type);

  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={video.title ? `${playLabel}: ${video.title}` : playLabel}
      className={styles.poster}
    >
      {thumb ? (
        // Below-the-fold: lazy (no priority) so it stays off the critical path.
        // The custom CDN loader passes img.youtube.com through unchanged — no
        // Next optimizer round-trip.
        <Image
          src={thumb}
          alt=""
          fill
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 142px"
          className={styles.thumb}
        />
      ) : (
        <span aria-hidden className={styles.placeholder} />
      )}
      {/* Guide `.rvv-c img { opacity: .88 }` — the poster is dimmed so the
          white play disc reads against any thumbnail, and lifts on hover. */}
      <span className={styles.scrim} />
      {/* Guide `.rvv-play` — a 42px near-white disc with a heading-tone
          triangle, nudged 2px right so the glyph looks centred. */}
      <span aria-hidden className={styles.play}>
        <Play strokeWidth={1.75} fill="currentColor" />
      </span>
    </button>
  );
}
